import { useState } from 'react';
import type { DirtyScenario, ScenarioType } from './callSettings';
import type { ScenarioSettingsDomain } from './settingsDomainTypes';
import type { FormSnapshot } from './types';
import { useClinicSettingsMutations } from './useClinicSettingsMutations';

export const useClinicSettingsSave = (
  clinicId: string | undefined,
  currentSnapshot: FormSnapshot,
  domain: ScenarioSettingsDomain,
) => {
  const { scenario, callSchedule, dirty, callScheduleIdRef } = domain;
  const { saveClinic, createNewSchedule, patchScenario } =
    useClinicSettingsMutations(clinicId, currentSnapshot);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!clinicId) return;
    setSaving(true);
    const isScheduleDirty = dirty.computeIsScheduleDirty(currentSnapshot);
    const isClinicBasicDirty = dirty.computeIsClinicBasicDirty(currentSnapshot);
    const isSlotDirty = dirty.computeIsSlotDirty(currentSnapshot);

    scenario.cacheCurrentValuesWithSchedule(
      callScheduleIdRef.current,
      callSchedule.schedule,
      callSchedule.holidays,
    );
    const dirtyScenarios = scenario.getDirtyScenarios(
      callScheduleIdRef.current,
      callSchedule.schedule,
      callSchedule.holidays,
    );

    try {
      if (isScheduleDirty || isClinicBasicDirty) {
        await saveClinic(isScheduleDirty);
      }

      const newScheduleIds = new Map<ScenarioType, string>();
      const savePromises = dirtyScenarios.map(async (entry) => {
        const scheduleId =
          entry.isScheduleDirty && entry.callSchedule
            ? await createNewSchedule(
                entry.callSchedule,
                entry.callHolidays ?? [],
              )
            : undefined;
        if (scheduleId) newScheduleIds.set(entry.type, scheduleId);
        await patchScenario(entry, scheduleId);
      });

      if (
        isSlotDirty &&
        !dirtyScenarios.some((entry) => entry.type === 'slot')
      ) {
        const cached = scenario.getCachedSlot();
        const slotScenario: DirtyScenario = {
          type: 'slot',
          callsMaxTries: cached.callsMaxTries,
          callsIntervalMinutes: cached.callsIntervalMinutes,
          callsDisable: cached.callsDisable,
          callsScheduleId: cached.callsScheduleId,
          callSchedule: cached.callSchedule,
          callHolidays: cached.callHolidays,
          isScheduleDirty: false,
          isFieldsDirty: false,
        };
        savePromises.push(patchScenario(slotScenario));
      }

      const results = await Promise.allSettled(savePromises);
      const rejected = results.find((result) => result.status === 'rejected');
      if (rejected?.status === 'rejected') throw rejected.reason;

      const committedScenarios = dirtyScenarios.map((entry) => {
        const scheduleId = newScheduleIds.get(entry.type);
        return scheduleId ? { ...entry, callsScheduleId: scheduleId } : entry;
      });
      scenario.commitDirtyToServer(committedScenarios);

      const activeNewId = newScheduleIds.get(scenario.scenarioType);
      if (activeNewId) callScheduleIdRef.current = activeNewId;
      scenario.cacheCurrentValuesWithSchedule(
        callScheduleIdRef.current,
        callSchedule.schedule,
        callSchedule.holidays,
      );
      dirty.updateSnapshot(currentSnapshot);
    } catch {
      // Ошибки обработаны через extraReducers → status.requestError
    } finally {
      setSaving(false);
    }
  };

  return { saving, handleSave };
};
